import { useState } from 'react';
import { trpc } from '@/lib/trpc';
import { AnalyticsCard } from './AnalyticsCard';
import { CustomButton } from '@/components/uiWrappers/button';
import { GoUpload } from 'react-icons/go';


export function UserActivityAnalytics() {
  const [isExporting, setIsExporting] = useState(false);
  const { data: summaryData } = trpc.admin.analytics.getAnalyticsSummary.useQuery();
  const { data: userActivityData } = trpc.admin.analytics.getUserActivityAnalytics.useQuery();
  const trpcUtils = trpc.useUtils();

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const csvData = await trpcUtils.admin.analytics.exportUserActivityCSV.fetch();

      if (csvData) {
        // Create timestamp for filename
        const now = new Date();
        const timestamp = now.toISOString().replace(/[:.]/g, '-').slice(0, -5);

        const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `user_activity_analytics_${timestamp}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error('Export failed:', error);
    } finally {
      setIsExporting(false);
    }
  };

  // Calculate summary stats
  const activeAccounts = summaryData?.activeUsers ?? 0;
  const users = userActivityData?.users || [];
  const totalUsers = users.length;
  const engagedUsers = users.filter(u => u.completedQuizzes > 0 || u.articlesOpened.total > 0).length;
  const avgAppOpens = totalUsers > 0 ? (users.reduce((sum, u) => sum + u.appOpens, 0) / totalUsers).toFixed(1) : '0';
  const avgQuizzes = totalUsers > 0 ? (users.reduce((sum, u) => sum + u.completedQuizzes, 0) / totalUsers).toFixed(1) : '0';
  const avgArticles = totalUsers > 0 ? (users.reduce((sum, u) => sum + u.articlesOpened.total, 0) / totalUsers).toFixed(1) : '0';

  return (
    <div className="space-y-6">
      {/* Export Button */}
      <div className="flex justify-end">
        <CustomButton
          type="button"
          onClick={handleExport}
          disabled={isExporting}
          variant="outline"
          size="lg"
          className="rounded-lg flex items-center gap-2"
        >
          <GoUpload className="size-5 text-primary" data-icon="inline-start" />
          Export
        </CustomButton>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      <AnalyticsCard
        label="Active Accounts"
        value={activeAccounts}
        tooltip="Current number of registered user accounts in the system"
      />
      <AnalyticsCard
        label="Engaged Users"
        value={engagedUsers}
        tooltip="Users who have completed at least 1 quiz OR read at least 1 article (includes deleted accounts with activity data)"
      />
      <AnalyticsCard
        label="Avg App Opens"
        value={avgAppOpens}
        tooltip="Average number of times users have opened the app"
      />
      <AnalyticsCard
        label="Avg Quizzes Completed"
        value={avgQuizzes}
        tooltip="Average number of quizzes completed per user"
      />
      <AnalyticsCard
        label="Avg Articles Read"
        value={avgArticles}
        tooltip="Average number of article reads per user"
      />
      </div>
    </div>
  );
}
